import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from "primeng/dynamicdialog";
import type { MenuItem } from "primeng/api";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { FLAT_SETTINGS, type SettingControlModel } from "@medsurf/flat-services";
import { PageTitleDialogComponent } from "../page-title-dialog/page-title-dialog.component";
import { NodeTreeModalComponent } from "../node-tree-modal/node-tree-modal.component";

@Component({
    selector: 'medsurf-toolbar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    standalone: false
})
export class ToolbarComponent {
  @Input() showNodeTreeButton = false;
  
  typedNodeExpanded: { [id: string]: boolean } = {};
  typedSlideAnnotationsExpanded: { [id: string]: boolean } = {};
  typedImageAnnotationExpanded: { [id: string]: boolean } = {};
	addSlideMenuItems: MenuItem[] = [
    {
      label: this.translate.instant('global.slide_add'),
      disabled: true,
      styleClass: 'menu_grouping_label',
    },
    {
      label: this.translate.instant('global.slide'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_SLIDE,
      command: () => this.addSlide(),
    },
    {
      label: this.translate.instant('global.training'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_TRAINING,
      command: () => this.addSlide(true),
    },
  ];
  
	getMainPanelDisplayMenuItems = () => {
		const mainPanelDisplay = this.slideAuthorFacade.snapshot_mainPanelDisplay();
		const { all, text, image } = FlatModels.SlideAuthorModels.MainPanelDisplay.enum;
		const iconClass = [all, text, image].includes(mainPanelDisplay)
			? FlatModels.ButtonDesignModels.ButtonDefaultIconsStyle.INFO
			: undefined;
		return [
			{
				label: this.translate.instant('global.main_panel_display_all'),
				disabled: mainPanelDisplay === all,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_SLIDE,
				iconClass,
				command: () => this.toggleMainPanelDisplay(all),
			},
			{
				label: this.translate.instant('global.main_panel_display_text'),
				disabled: mainPanelDisplay === text,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.TEXT,
				iconClass,
				command: () => this.toggleMainPanelDisplay(text),
			},
			{
				label: this.translate.instant('global.main_panel_display_image'),
				disabled: mainPanelDisplay === image,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_VISUAL,
				iconClass,
				command: () => this.toggleMainPanelDisplay(image),
			}
		] satisfies MenuItem[];
	}
  readonly FlatModels = FlatModels;
	
  constructor(protected alertControlFacade: FlatFacades.AlertControlFacade,
              protected slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              protected bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              protected appSharedFacade: FlatFacades.AppSharedFacade,
              protected nodeSharedFacade: FlatFacades.NodeSharedFacade,
              protected historySharedFacade: FlatFacades.HistorySharedFacade,
              private dialogService: DialogService,
              private translate: TranslateService,
              @Inject(FLAT_SETTINGS) private settings: SettingControlModel) {
    this.slideAuthorFacade.typedNodeExpanded$.pipe(takeUntilDestroyed()).subscribe((typedNodeExpanded) => {
      this.typedNodeExpanded = typedNodeExpanded;
    });
    this.slideAuthorFacade.typedSlideAnnotationsExpanded$.pipe(takeUntilDestroyed()).subscribe((typedSlideAnnotationsExpanded) => {
      this.typedSlideAnnotationsExpanded = typedSlideAnnotationsExpanded;
    });
    this.slideAuthorFacade.typedImageAnnotationExpanded$.pipe(takeUntilDestroyed()).subscribe((typedImageAnnotationExpanded) => {
      this.typedImageAnnotationExpanded = typedImageAnnotationExpanded;
    });
  }
  
  executeUndo() {
    if (this.historySharedFacade.snapshot_canUndo()) {
      this.historySharedFacade.requestUndoSnapshot().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  executeRedo() {
    if (this.historySharedFacade.snapshot_canRedo()) {
      this.historySharedFacade.requestRedoSnapshot().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  executeSave() {
    if (this.historySharedFacade.snapshot_canSave()) {
      this.historySharedFacade.requestSaveSnapshots().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => {
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    }
  }
  
  openNodePreview(routeElement: FlatModels.NodeEntityModels.RouteElement) {
    if (this.historySharedFacade.snapshot_canSave()) {
      this.historySharedFacade.requestSaveSnapshots().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => {
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
          // Navigate To Url
          const url = `${this.settings.viewerUrl}${routeElement.path}`;
          open(url, '_medsurf_preview');
        }
      });
    } else {
      // Navigate To Url
      const url = `${this.settings.viewerUrl}${routeElement.path}`;
      open(url, '_medsurf_preview');
    }
  }
  
  openNodeTreeModal() {
    this.dialogService.open(NodeTreeModalComponent, {
      position: 'bottom-left',
      header: this.translate.instant('global.contents'),
      dismissableMask: true,
      width: '30%',
      height: '100%',
    });
  }
  
  copySlide(id: string) {
    this.bulkEditAuthorFacade.requestCopyNode(id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  addSlide(isTraining = false) {
    const routeElement = structuredClone(this.nodeSharedFacade.snapshot_currentSelectedRouteElement());
    const parentRouteElement = structuredClone(this.nodeSharedFacade.snapshot_currentSelectedParentRouteElement());
    if (!parentRouteElement) return;
    const dialogRef = this.dialogService.open(PageTitleDialogComponent, {
      header: this.translate.instant(isTraining ? 'global.training_add_description' : 'global.slide_add_description'),
      data: {
        inputLabel: this.translate.instant('global.add')
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(data => {
      if (data?.result && parentRouteElement) {
        this.nodeSharedFacade.requestAddNodeAfterCurrent(parentRouteElement, routeElement, 'history.node_add', data.title, isTraining)
          .subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
      }
    });
  }
  
  toggleMainPanelDisplay(type: FlatModels.SlideAuthorModels.MainPanelDisplay) {
    this.slideAuthorFacade.requestSetMainPanelDisplay(type).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
}
